import { useEffect, useRef, useState } from 'react';

import {
  createSupportIssue,
  type ISupportIssueAttachment,
} from '@commenda/api';
import { Button, Icons, Tooltip, toast } from '@commenda/kakashi';

import { AttachmentPill } from './AttachmentPill';
import { ChatIconButton } from './ChatIconButton';

type SendState = 'idle' | 'sending' | 'sent';

/** A support report the agent has already written from the turn that just failed, shown for the
 * user to approve rather than retype. Nothing is filed until Send: the agent's tool call returned
 * this draft without filing, and Send posts it to the same `pylon/support-issue` endpoint the
 * Help and Support modal uses. Rendered like an email compose card — recipient row, subject and
 * body — so it reads as the message that goes out. */
interface IssueDraftCardProps {
  subject: string;
  message: string;
  companyId: number | null;
  destination: string | null;
  /** The chat's files the agent attached to the draft. The user can drop any before sending. */
  files: ISupportIssueAttachment[];
}

export function IssueDraftCard({
  subject,
  message,
  companyId,
  destination,
  files,
}: Readonly<IssueDraftCardProps>) {
  const [sendState, setSendState] = useState<SendState>('idle');
  const [attachments, setAttachments] = useState(files);
  const [copied, setCopied] = useState(false);
  const copyResetRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  const copyDraft = () => {
    navigator.clipboard
      .writeText(`${subject}\n\n${message}`)
      .then(() => {
        setCopied(true);
        clearTimeout(copyResetRef.current);
        copyResetRef.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  const removeAttachment = (fileUrl: string) =>
    setAttachments((current) =>
      current.filter((attachment) => attachment.fileUrl !== fileUrl)
    );

  const send = async () => {
    if (companyId === null) {
      toast.error(
        'This report is not attached to a company. Email operations@commenda.io instead.'
      );
      return;
    }

    setSendState('sending');
    const result = await createSupportIssue({
      companyId,
      subject,
      message,
      attachments,
    });

    if (!result.success) {
      setSendState('idle');
      toast.error(
        result.error ||
          'Could not send the report. Email operations@commenda.io instead.'
      );
      return;
    }

    setSendState('sent');
    toast.success('Report sent. Commenda replies by email.');
  };

  return (
    <div className="flex w-full flex-col divide-y divide-border-secondary overflow-hidden rounded-xl border-[0.5px] border-border-primary bg-bg-primary shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]">
      <div className="flex items-center gap-sm px-lg py-md">
        <span className="flex-1 txt-small-medium text-text-primary">
          Issue draft
        </span>
        <Tooltip content={copied ? 'Copied' : 'Copy'} asChild>
          <ChatIconButton
            aria-label={copied ? 'Copied' : 'Copy'}
            onClick={copyDraft}
            icon={
              copied ? (
                <Icons.Check className="size-4" aria-hidden="true" />
              ) : (
                <Icons.Copy06 className="size-4" aria-hidden="true" />
              )
            }
          />
        </Tooltip>
        <Button
          variant="secondary"
          size="sm"
          onClick={send}
          disabled={sendState !== 'idle'}
          isLoading={sendState === 'sending'}
          loadingLabel="Sending…"
          leftIcon={
            sendState === 'sent' ? (
              <Icons.Check className="size-4" aria-hidden="true" />
            ) : (
              <Icons.Send01 className="size-4" aria-hidden="true" />
            )
          }
          className="shrink-0"
        >
          {sendState === 'sent' ? 'Sent' : 'Send'}
        </Button>
      </div>

      <div className="flex items-baseline gap-md px-lg py-md">
        <span className="shrink-0 txt-small-regular text-text-tertiary">
          To
        </span>
        <span className="txt-small-regular text-text-primary">
          {destination ?? 'Commenda support'}
        </span>
      </div>

      <div className="flex flex-col gap-lg px-lg py-lg">
        <span className="txt-small-medium text-text-primary">{subject}</span>
        <span className="whitespace-pre-wrap txt-small-regular text-text-primary">
          {message}
        </span>
        {attachments.length > 0 && (
          <div className="flex flex-wrap items-center gap-sm">
            {attachments.map((attachment) => (
              <AttachmentPill
                key={attachment.fileUrl}
                fileName={attachment.fileName}
                mimeType=""
                fileRef={attachment.fileUrl}
                onRemove={
                  sendState === 'idle'
                    ? () => removeAttachment(attachment.fileUrl)
                    : undefined
                }
              />
            ))}
          </div>
        )}
      </div>

      <div className="px-lg py-md txt-extra-small-regular text-text-tertiary">
        {sendState === 'sent'
          ? 'Sent. Commenda replies to your email.'
          : 'Nothing is sent until you press Send.'}
      </div>
    </div>
  );
}
